import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
  Calculator,
  Settings,
  Plus,
  Search,
  LayoutGrid,
  Moon,
  Sun,
  Droplets,
  UtensilsCrossed,
  BedDouble,
  DoorOpen,
  type LucideIcon,
} from "lucide-react"

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "../shared/ui/command"
import { useRooms } from "@/entities/room"
import { useUIStore } from "@/stores"
import { STARTER_TEMPLATES } from "@/entities/room"
import { useProjectStore } from "@/stores"

const TEMPLATE_ICON_MAP: Record<string, LucideIcon> = {
  tpl_bathroom: Droplets,
  tpl_kitchen: UtensilsCrossed,
  tpl_bedroom: BedDouble,
  tpl_hallway: DoorOpen,
}

export function CommandMenu() {
  const [open, setOpen] = React.useState(false)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { rooms, setActiveRoomId, addRoom } = useRooms()
  const toggleTheme = useUIStore((s) => s.toggleTheme)
  const openModal = useUIStore((s) => s.openModal)
  const addRoomFromTemplate = useProjectStore((s) => s.addRoomFromTemplate)

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  const runCommand = React.useCallback((command: () => void) => {
    setOpen(false)
    command()
  }, [])

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={t('common.search', 'Поиск...')} />
      <CommandList>
        <CommandEmpty>{t('common.nothing_found', 'Ничего не найдено')}</CommandEmpty>

        <CommandGroup heading={t('dashboard.tabs.projects', 'Проекты')}>
          <CommandItem onSelect={() => runCommand(() => navigate('/dashboard'))}>
            <LayoutGrid className="mr-2 h-4 w-4" />
            <span>{t('editor.sidebar.dashboard', 'Панель управления')}</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => addRoom())}>
            <Plus className="mr-2 h-4 w-4" />
            <span>{t('editor.empty.create_room_btn', 'Создать комнату')}</span>
            <CommandShortcut>N</CommandShortcut>
          </CommandItem>
        </CommandGroup>

        {/* Starter templates group — TMPL-01 */}
        <CommandSeparator />
        <CommandGroup heading={t('templates.command_group', 'Szablony startowe')}>
          {STARTER_TEMPLATES.map((template) => {
            const Icon = TEMPLATE_ICON_MAP[template.id] ?? DoorOpen
            return (
              <CommandItem
                key={template.id}
                onSelect={() =>
                  runCommand(() => {
                    const newId = addRoomFromTemplate(template.id)
                    setActiveRoomId(newId)
                    navigate('/')
                  })
                }
              >
                <Icon className="mr-2 h-4 w-4" aria-hidden="true" />
                <span>{t(template.nameKey, template.name)}</span>
              </CommandItem>
            )
          })}
        </CommandGroup>

        <CommandGroup heading={t('editor.room_tabs', 'Комнаты')}>
          {rooms.map((room, idx) => (
            <CommandItem
                key={room.id}
                onSelect={() => runCommand(() => {
                    setActiveRoomId(room.id);
                    navigate('/');
                })}
            >
              <Calculator className="mr-2 h-4 w-4" />
              <span>{room.name || t('editor.room_default_name', 'Комната {{n}}', { n: idx + 1 })}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t('dashboard.tabs.system', 'Система')}>
          <CommandItem onSelect={() => runCommand(() => openModal('service_selector'))}>
            <Search className="mr-2 h-4 w-4" />
            <span>{t('editor.service_btn', 'Добавить услугу')}</span>
            <CommandShortcut>S</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => toggleTheme())}>
            <Sun className="mr-2 h-4 w-4 dark:hidden" />
            <Moon className="mr-2 h-4 w-4 hidden dark:block" />
            <span>{t('common.theme_light', 'Смена темы')}</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/dashboard'))}>
            <Settings className="mr-2 h-4 w-4" />
            <span>{t('dashboard.tabs.system', 'Настройки системы')}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
